<template>
  <base-material-card color="primary" icon="mdi-calculator-variant-outline" inline class="ml-0 px-5 py-4">
    <template v-slot:after-heading>
      <div class="display-2 font-weight-light">
        {{ title }}
      </div>
    </template>

    <v-row class="mx-auto">
      <v-col cols="12" class="pb-0">
        <validation-provider v-slot="{ errors }" rules="required">
          <v-text-field
            id="nome-racionalizacao-com"
            v-model="nome"
            label="Nome"
            outlined
            :error-messages="errors"
            class="pt-5"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <saneamento-voc-choose-paramentros-com-ca
      divId="rac-com-cmu"
      titulo="CMU - Custo Médio Unitário"
      nomeParametro="CMU"
      :items="cmuExistentes"
      :campos.sync="campos"
      linkCadastros="/parametros-com-ca/cmu"
      parametro="cmu"
    />

    <saneamento-voc-choose-paramentros-com-ca
      divId="rac-com-normas"
      titulo="Normas Técnicas"
      nomeParametro="Normas Técnicas"
      :items="normasTecnicasExistentes"
      :campos.sync="campos"
      linkCadastros="/parametros-com-ca/normas-tecnicas"
      parametro="normasTecnicas"
    />

    <v-divider class="my-6" />

    <filtros-execucao :filtros.sync="filtros" />
  </base-material-card>
</template>

<script>
  import CmuService from '@/services/parametrosComCa/CmuService.js';
  import NormasTecnicasService from '@/services/parametrosComCa/NormasTecnicasService.js';
  import DateFormatterMixins from '@/mixins/dateFormatterMixins.js';

  const camposIniciais = () => ({
    cmu: {
      origem: 'existente',
      origemList: [
        { text: 'CMU Existente', value: 'existente' },
        { text: 'Arquivo CSV', value: 'novo' }
      ],
      nome: '',
      id: '',
      arquivo: {
        id: '',
        fileObj: undefined,
        arquivoId: '',
        delimitador: `;`,
        involucro: `"`,
        escape: `"`,
        uploading: false,
        headerValido: false,
        fakeFile: false
      },
      valores: {}
    },
    normasTecnicas: {
      origem: 'existente',
      origemList: [
        { text: 'Normas Técnicas Existente', value: 'existente' },
        { text: 'Arquivo CSV', value: 'novo' }
      ],
      nome: '',
      id: '',
      arquivo: {
        id: '',
        fileObj: undefined,
        arquivoId: '',
        delimitador: `;`,
        involucro: `"`,
        escape: `"`,
        uploading: false,
        headerValido: false,
        fakeFile: false
      },
      valores: {}
    }
  });

  const filtrosIniciais = () => ({
    tipoBase: 'AMBAS',
    obras: '',
    obrasNaoPertencem: false,
    dataImob: {
      inicio: '',
      fim: '',
      inicioFormatted: '',
      fimFormatted: ''
    },
    menuPickerImob: [false, false]
  });

  const parseFiltrosSalvos = (filtrosRaw, formatDateFromBackend) => {
    if (!filtrosRaw) {
      return filtrosIniciais();
    }

    const filtros = typeof filtrosRaw === 'string' ? JSON.parse(filtrosRaw || '{}') : filtrosRaw || {};
    const estado = filtrosIniciais();

    if (filtros.tipoBase) {
      const tipoBase = String(filtros.tipoBase).toUpperCase();
      if (['AMBAS', 'AIC', 'AIS'].includes(tipoBase)) {
        estado.tipoBase = tipoBase;
      }
    }

    estado.obras = filtros.obras || '';
    estado.obrasNaoPertencem = filtros.obrasNaoPertencemALista == 1;

    if (filtros.dataImobilizacaoInicial) {
      estado.dataImob.inicioFormatted = formatDateFromBackend(filtros.dataImobilizacaoInicial);
    }

    if (filtros.dataImobilizacaoFinal) {
      estado.dataImob.fimFormatted = formatDateFromBackend(filtros.dataImobilizacaoFinal);
    }

    return estado;
  };

  const buildFiltrosExport = (filtros) => {
    const exportFiltros = {};

    if (filtros.tipoBase && filtros.tipoBase !== 'AMBAS') {
      exportFiltros.tipoBase = filtros.tipoBase;
    }

    if (filtros.obras && filtros.obras.trim()) {
      exportFiltros.obras = filtros.obras.trim();
      if (filtros.obrasNaoPertencem) {
        exportFiltros.obrasNaoPertencemALista = 1;
      }
    }

    if (filtros.dataImob.inicioFormatted) {
      exportFiltros.dataImobilizacaoInicial = filtros.dataImob.inicioFormatted;
    }

    if (filtros.dataImob.fimFormatted) {
      exportFiltros.dataImobilizacaoFinal = filtros.dataImob.fimFormatted;
    }

    return exportFiltros;
  };

  export default {
    name: 'ParametrosRacionalizacaoCom',
    components: {
      SaneamentoVocChooseParamentrosComCa: () => import('@/components/saneamentoVoc/SaneamentoVocChooseParamentrosComCa.vue'),
      FiltrosExecucao: () => import('@/components/racionalizacaoCom/FiltrosExecucao.vue')
    },
    mixins: [DateFormatterMixins],
    props: {
      item: {
        type: Object,
        default: () => ({})
      },
      title: {
        type: String,
        default: ''
      }
    },
    data: () => ({
      nome: '',
      cmuExistentes: [],
      normasTecnicasExistentes: [],
      campos: camposIniciais(),
      filtros: filtrosIniciais()
    }),
    mounted() {
      this.loadParametros();
    },
    methods: {
      loadParametros() {
        CmuService.getAllValoresUnitarios()
          .then((res) => {
            this.cmuExistentes = res.data.valores;
          })
          .catch(() => this.$toast.error('Erro ao recuperar CMUs.', '', { position: 'topRight' }));

        NormasTecnicasService.getAllNormasTecnicas()
          .then((res) => {
            this.normasTecnicasExistentes = res.data.normas;
          })
          .catch(() => this.$toast.error('Erro ao recuperar Normas Técnicas.', '', { position: 'topRight' }));
      },
      formatDateFromBackend(value) {
        if (!value) {
          return '';
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
          return value;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const [ano, mes, dia] = value.split('-');
          return `${dia}/${mes}/${ano}`;
        }

        return value;
      },
      fillFields() {
        if (!this.item || !Object.keys(this.item).length) {
          return;
        }

        const parametros =
          typeof this.item.parametros === 'string'
            ? JSON.parse(this.item.parametros || '{}')
            : this.item.parametros || {};

        this.nome = this.item.nome || '';
        this.campos = camposIniciais();
        this.filtros = parseFiltrosSalvos(this.item.filtros, this.formatDateFromBackend);

        if (this.filtros.dataImob.inicioFormatted) {
          this.filtros.dataImob.inicio = this.parseDate(this.filtros.dataImob.inicioFormatted);
        }

        if (this.filtros.dataImob.fimFormatted) {
          this.filtros.dataImob.fim = this.parseDate(this.filtros.dataImob.fimFormatted);
        }

        if (this.item.cmu_id) {
          this.campos.cmu.origem = 'existente';
          this.campos.cmu.id = this.item.cmu_id;
        } else if (parametros.siglas_aneel_valores) {
          this.campos.cmu.origem = 'novo';
          this.campos.cmu.valores = parametros.siglas_aneel_valores;
        }

        if (this.item.normas_tecnicas_id) {
          this.campos.normasTecnicas.origem = 'existente';
          this.campos.normasTecnicas.id = this.item.normas_tecnicas_id;
        } else if (parametros.normas_quantidades_com) {
          this.campos.normasTecnicas.origem = 'novo';
          this.campos.normasTecnicas.valores = parametros.normas_quantidades_com;
        }
      },
      exportData() {
        return {
          nome: this.nome,
          filtros: buildFiltrosExport(this.filtros),
          parametros: {
            siglas_aneel_valores: this.campos.cmu.valores,
            normas_quantidades_com: this.campos.normasTecnicas.valores
          },
          cmu_id: this.campos.cmu.origem === 'novo' ? '' : this.campos.cmu.id,
          normas_tecnicas_id: this.campos.normasTecnicas.origem === 'novo' ? '' : this.campos.normasTecnicas.id
        };
      }
    },
    watch: {
      item: {
        handler() {
          this.fillFields();
        },
        deep: true,
        immediate: true
      }
    }
  };
</script>
